// NOTE: dont export outside package scope

import { ReactElement } from 'react'

export type PromptId = number | string

export interface PromptEntry {
  id: PromptId
  prompt: ReactElement
}

export type PromptsAction =
  | { type: 'PROMPT'; id: PromptId; prompt: ReactElement }
  | { type: 'ANSWER'; id: PromptId }

export type PromptsDispatch = (action: PromptsAction) => void

export interface PromptsStore {
  dispatch: PromptsDispatch
  getSnapshot: () => PromptEntry[]
  subscribe: (listener: () => void) => () => void
}

export const promptsReducer = (
  entries: PromptEntry[],
  action: PromptsAction,
): PromptEntry[] => {
  switch (action.type) {
    case 'ANSWER': {
      const { id } = action
      const remaining = entries.filter((entry) => entry.id !== id)

      // every unmount with autoClose answers, most of them without a prompt
      return remaining.length === entries.length ? entries : remaining
    }
    case 'PROMPT': {
      const { id, prompt } = action
      const notFound = entries.findIndex((entry) => entry.id === id) === -1

      if (notFound) {
        return [...entries, { id, prompt }]
      }

      return entries
    }
    default:
      return entries
  }
}

/**
 * Connects `usePrompt` callers with the `Prompts` renderers of the same tree.
 * The entries live in the store and not in a renderer, because `Prompts`
 * subscribes in an effect that runs after the effects of its siblings, so a
 * prompt opened on mount would be lost on the way to a renderer that is not
 * listening yet.
 */
export const createPromptsStore = (): PromptsStore => {
  let entries: PromptEntry[] = []
  let listeners: (() => void)[] = []

  return {
    dispatch: (action) => {
      const nextEntries = promptsReducer(entries, action)

      if (nextEntries === entries) {
        return
      }

      entries = nextEntries
      listeners.forEach((listener) => listener())
    },
    getSnapshot: () => entries,
    subscribe: (listener) => {
      listeners = [...listeners, listener]

      return () => {
        listeners = listeners.filter((item) => item !== listener)
      }
    },
  }
}
